<template>
  <div class="loan-beacon-widget">
    <CalicoBanner
      v-if="!loading && editor && showBanner"
      variant="info"
      @close="hideBanner = true"
    >
      Complete this section.
    </CalicoBanner>
    <CalicoLoanBeaconIndicator 
      v-if="!loading && editor" 
      :disabled="!isProfileCompleted"
      :value="loanBeacon.isActive" 
      @toggle="toggle"
    />
    <div
      v-else-if="!loading && loanBeacon.isActive"
      class="loan-beacon-header"
    >
      <i class="bi bi-currency-dollar contained-icon-medium-outlined" />
      <h2 class="heading--xsmall">
        This business is seeking a loan
      </h2>
    </div>

    <span
      v-if="!loading && editor && loanBeacon.isActive"
      class="body--small"
    >
      Lenders are able view your loan profile and financial documents. 
    </span>
    <span
      v-else-if="!loading && editor"
      class="body--small"
    >
      Let lenders know you’re looking for a loan. Provide helpful financial information to increase your qualification.
    </span>
    <span
      v-else-if="!loading && loanBeacon.isActive"
      class="body--small"
    >
      Contact them directly for more information.
    </span>

    <div
      v-if="!loading && editor && !isProfileCompleted"
      class="start-container"
    >
      <div
        v-if="!isLoanProfileCompleted"
        class="loan-start"
      >
        <div class="loan-start-text">
          <label class="label--medium">
            Loan Profile
          </label>
          <span class="body--xsmall action-description">
            Amount, Use, Annual Revenue, Personal Credit Score
          </span>
        </div>
        <span style="width: fit-content;">
          <button 
            class="button-primary button-primary--small" 
            @click="openLoanProfileEditor"
          >
            Complete
          </button>
        </span>
      </div>
      <div
        v-if="!isDocumentsUploaded"
        class="loan-start"
      >
        <div class="loan-start-text">
          <label class="label--medium">
            Financial Documents
          </label>
          <span class="body--xsmall action-description">
            Income Statement, Balance Sheet, Bank Statements, Tax Transcripts.
          </span>
        </div>
        <span style="width: fit-content;">
          <button 
            class="button-primary button-primary--small" 
            @click="openDocuments"
          >
            Start
          </button>
        </span>
      </div>
    </div>

    <CalicoLoanProfile
      v-if="!loading && showLoanProfile"
      :amount="loanBeacon.loanAmount"
      :type="loanBeacon.loanType"
      :use="loanBeacon.loanUse"
      :personal-credit-score="loanBeacon.personalCreditScore"
      :edit="editor"
      @edit="openLoanProfileEditor"
    />

    <span
      v-if="!loading && showFinancialDocuments"
      style="width: fit-content;"
    >
      <button
        class="button-utility button-utility--large"
        @click="openDocuments"
      >
        <i class="bi bi-folder" />
        Financial Documents ({{ documentsCount }})
      </button>
    </span>

    <CalicoLoader v-if="loading" />

    <Modal 
      v-if="showDocuments" 
      header="Funding Passport™" 
      full-screen 
      @close="closeDocuments"
    >
      <template #content>
        <CalicoFinancialDocuments :error="error">
          <CalicoDocumentSection
            title="Income Statement"
            description="An income statement, also known as the profit and loss (P&L) statement, summarizes a company's revenue, expenses, gains, and losses over a specific accounting period."
            :documents="documents.incomeStatement"
            :draft-document="drafts.incomeStatement"
            :editable="editor"
            @browse="file => browse('incomeStatement', file)"
            @edit="edit"
            @download="download"
            @delete="onDelete"
            @save="save"
            @cancel="cancel"
          />

          <CalicoDocumentSection
            title="Balance Sheet"
            description="A balance sheet is a financial statement that shows what a company owns, what it owes, and the amount invested by shareholders at a specific point in time."
            :documents="documents.balanceSheet"
            :draft-document="drafts.balanceSheet"
            :editable="editor"
            @browse="file => browse('balanceSheet', file)"
            @edit="edit"
            @download="download"
            @delete="onDelete"
            @save="save"
            @cancel="cancel"
          />

          <CalicoDocumentSection
            title="Cash Flow Statement"
            description="A cash flow statement shows how money flows in and out of a company through operations, investments, and financing activities."
            :documents="documents.cashFlow"
            :draft-document="drafts.cashFlow"
            :editable="editor"
            @browse="file => browse('cashFlow', file)"
            @edit="edit"
            @download="download"
            @delete="onDelete"
            @save="save"
            @cancel="cancel"
          />

          <CalicoDocumentSection
            title="Other documents"
            description="Include other documents that might not fit in the above formats, but still demonstrate your business’ financial activity (like a hand-written ledger.)"
            :documents="documents.other"
            :draft-document="drafts.other"
            :editable="editor"
            @browse="file => browse('other', file)"
            @edit="edit"
            @download="download"
            @delete="onDelete"
            @save="save"
            @cancel="cancel"
          />
        </CalicoFinancialDocuments>
      </template>
    </Modal>

    <Modal
      v-if="showLoanProfileEditor"
      header="Funding Passport™"
      full-screen
      @close="onCloseLoanProfileEditor"
    >
      <template #content>
        <ModalIntake
          :key="$route.path"
          type="loan-beacon"
          :loading="loading"
          :error="error"
          full-screen
          @progress="onProgress"
          @save="onSave"
        >
          <template #business-funding-passport-loan-information>
            <div class="loan-information">
              <CalicoForm
                v-slot="{ field, error }"
                name="loanAmount"
                label="Loan Amount"
                rules="required"
              >
                <CalicoField
                  v-model="formData.loanAmount"
                  v-bind="field"
                  :error="!!error"
                  :only-numbers="true"
                  :symbol="'$'"
                  :custom-format="formatAndRoundCurrency"
                />
              </CalicoForm>

              <p class="body--xsmall hint">
                {{ hint }}
              </p>

              <CalicoForm
                v-slot="{ field, error }"
                name="loanType"
                label="Loan Type"
                rules="required"
              >
                <CalicoDropdown
                  v-model="formData.loanType"
                  v-bind="field"
                  :error="!!error"
                  placeholder="Select..."
                >
                  <option
                    v-for="option in loanTypeOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </CalicoDropdown>
              </CalicoForm>

              <CalicoForm
                v-slot="{ field, error }"
                name="loanUse"
                label="Loan Use"
                rules="required"
              >
                <CalicoDropdown
                  v-model="formData.loanUse"
                  v-bind="field"
                  :error="!!error"
                  placeholder="Select..."
                >
                  <option
                    v-for="option in loanUseOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </CalicoDropdown>
              </CalicoForm>
            </div>
          </template>
          <template #business-funding-passport-personal-credit-score>
            <CalicoForm
              v-model="confirmPersonalCreditScore"
              label="Are you the business owner?"
            >
              <CalicoRadioButton
                v-model="confirmPersonalCreditScore"
                value="yes"
              >
                Yes
              </CalicoRadioButton>
              <CalicoRadioButton
                v-model="confirmPersonalCreditScore"
                value="no"
              >
                No
              </CalicoRadioButton>
            </CalicoForm>
            <CalicoForm
              v-if="confirmPersonalCreditScore === 'yes'"
              label="What is your personal credit score?"
            >
              <CalicoRadioButton
                v-for="option in personalCreditScoreOptions"
                :key="option.value"
                v-model="formData.personalCreditScore"
                :value="option.value"
              >
                {{ option.label }}
              </CalicoRadioButton>
            </CalicoForm>

            <CalicoForm
              v-if="confirmPersonalCreditScore === 'no'"
              label="What is the personal credit score of the business owner?"
            >
              <CalicoRadioButton
                v-for="option in personalCreditScoreOptions"
                :key="option.value"
                v-model="formData.personalCreditScore"
                :value="option.value"
              >
                {{ option.label }}
              </CalicoRadioButton>
              <CalicoRadioButton
                v-model="formData.personalCreditScore"
                value="Unknown"
              >
                I'll do this later
              </CalicoRadioButton>
            </CalicoForm>
          </template>
        </ModalIntake>
      </template>
    </Modal>
  </div>
</template>

<script>
import CalicoField from "@/components/calico/field/index.vue"
import CalicoDropdown from "@/components/calico/dropdown/index.vue"
import CalicoRadioButton from "@/components/calico/radioButton/index.vue"
import CalicoBanner from "@/components/calico/banner/index.vue"
import CalicoLoanBeaconIndicator from "@/components/calico/loanBeaconIndicator/index.vue"
import CalicoLoader from '@/components/calico/loader/index.vue';
import CalicoDocumentSection from "@/components/pattern/documentSection/index.vue"
import CalicoFinancialDocuments from "@/components/pattern/financialDocuments/index.vue"
import CalicoForm from "@/components/pattern/form/index.vue"
import ModalIntake from '@/components/pattern/intake/index.vue'
import CalicoLoanProfile from '@/components/pattern/loanProfile/index.vue'
import Modal from "@/components/template/modal/index.vue"
import services from "@/services"
import { RoutesEnum } from '@/enums'
import { formatAndRoundCurrency } from '@/utils/helper'
import { loanTypeOptions, loanUseOptions, personalCreditScoreOptions } from '@/constants'

const DOCUMENT_CONFIG = {
  incomeStatement: {
    type: "income-statement",
    category: "INCOME_STATEMENT",
    source: "LOAN_BEACON_FINANCIALS",
    label: "Income Statement",
  },
  balanceSheet: {
    type: "balance-sheet",
    category: "BALANCE_SHEET",
    source: "LOAN_BEACON_FINANCIALS",
    label: "Balance Sheet",
  },
  cashFlow: {
    type: "cash-flow",
    category: "CASH_FLOW_STATEMENT",
    source: "LOAN_BEACON_FINANCIALS",
    label: "Cash Flow",
  },
  other: {
    type: "other",
    category: "OTHER",
    source: "LOAN_BEACON_OTHER",
    label: "Other documents",
  },
}

export default {
  name: 'LoanBeaconWidget',
  components: {
    CalicoField,
    CalicoDropdown,
    CalicoRadioButton,
    CalicoBanner,
    CalicoLoanBeaconIndicator,
    CalicoLoader,
    CalicoDocumentSection,
    CalicoFinancialDocuments,
    CalicoForm,
    ModalIntake,
    CalicoLoanProfile,
    Modal
  },
  props: {
    data: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      documents: {
        incomeStatement: [],
        balanceSheet: [],
        cashFlow: [],
        other: []
      },
      documentsCount: 0,
      drafts: {
        incomeStatement: null,
        balanceSheet: null,
        cashFlow: null,
        other: null,
      },
      formData: {
        loanAmount: null,
        loanType: '',
        loanUse: '',
        personalCreditScore: ''
      },
      business: {},
      progresses: [],
      loanBeacon: {},
      editor: false,
      showDocuments: false,
      error: "",
      loading: false,
      confirmPersonalCreditScore: '',
      isLoanProfileCompleted: false,
      hideBanner: false,
      RoutesEnum,
      formatAndRoundCurrency,
      loanUseOptions,
      loanTypeOptions,
      personalCreditScoreOptions,
    }
  },
  computed: {
    isDocumentsUploaded () {
      return this.documents.incomeStatement[0] || this.documents.balanceSheet[0] || this.documents.cashFlow[0] || this.documents.other[0]
    },
    showLoanProfileEditor () {
      return this.$route.path.startsWith('/business/funding-passport');
    },
    isProfileCompleted () {
      return this.isLoanProfileCompleted && this.isDocumentsUploaded
    },
    showLoanProfile () {
      if (this.editor) {
        return this.isLoanProfileCompleted
      } else {
        return this.loanBeacon.isActive
      }
    },
    showBanner () {
      return !this.isProfileCompleted && !this.hideBanner
    },
    showFinancialDocuments () {
      if (this.editor) {
        return this.isDocumentsUploaded
      } else {
        return this.loanBeacon.isActive
      }
    }
  },
  watch: {
    showLoanProfileEditor(val, oldVal) {
      if (oldVal === true && val === false) {
        this.onCloseLoanProfileEditor()
      }
    }
  },
  async mounted () {
    try {
      this.loading = true
      if (this.$route.params.id) {
        this.business = this.data || await services.business.get(this.$route.params.id)
        this.editor = false
      } else {
        this.business = this.data || await services.user.business.get()
        this.editor = true
      }
      this.loanBeacon = await services.loanBeacon.get(this.business.id)
      const { loanAmount, loanType, loanUse, personalCreditScore } = this.loanBeacon
      this.formData = {
        loanAmount: loanAmount ?? null,
        loanType: loanType ?? '',
        loanUse: loanUse ?? '',
        personalCreditScore: personalCreditScore ?? ''
      }
      await this.loadDocuments()
    } catch (e) {
      this.editor = false
      console.log("Error occured while initializing loan beacon")
    } finally {
      const progresses = await services.progress.get('loan-beacon')
      this.onProgress(progresses)
      this.loading = false
    }
  },
  methods: {
    async toggle () {
      this.loanBeacon = await services.loanBeacon.toggle(this.business.id)
    },

    async loadDocuments () {
      this.error = ""
      const { documents, total } = await services.loanBeacon.loanDocuments.grouped(this.business.id)
      this.documents = documents
      this.documentsCount = total
    },

    browse (sectionKey, file) {
      if (!file) return

      const config = DOCUMENT_CONFIG[sectionKey]

      this.drafts[sectionKey] = {
        id: null,
        sectionKey,
        name: file.name,
        type: config.type,
        mimeType: file.type || "",
        sizeBytes: file.size || 0,
        year: String(new Date().getFullYear()),
        description: "",
        category: config.category,
        source: config.source,
        visibility: "PUBLIC",
        file,
      }
    },

    onCloseLoanProfileEditor () {
      if (this.$route.path !== RoutesEnum.BUSINESS_DASHBOARD) {
        this.$router.push(RoutesEnum.BUSINESS_DASHBOARD)
      }
    },

    openLoanProfileEditor () {
      this.$router.push(RoutesEnum.LOAN_BEACON_LOAN_INFORMATION)
    },

    async save (document) {
      this.error = ""

      if (!document) return

      if (document.id) {
        const updated = await services.user.loanDocuments.update(document.id, {
          year: document.year,
          description: document.description,
          category: document.category,
          source: document.source,
          visibility: document.visibility,
        })

        if (!updated) {
          this.error = "Unable to update document."
          return
        }

        await this.loadDocuments()
        return
      }

      const config = DOCUMENT_CONFIG[document.sectionKey]

      const uploaded = await services.user.loanDocuments.upload(
        config.type,
        document.file,
        {
          year: document.year,
          description: document.description,
          category: config.category,
          source: config.source,
          visibility: document.visibility || "PUBLIC",
        }
      )

      if (!uploaded) {
        this.error = "Unable to upload document."
        return
      }

      this.drafts[document.sectionKey] = null
      await this.loadDocuments()
    },

    cancel (document) {
      if (!document?.id && document?.sectionKey) {
        this.drafts[document.sectionKey] = null
      }
    },

    async onDelete (document) {
      this.error = ""

      if (!document?.id) return

      const deleted = await services.user.loanDocuments.delete(document.id)

      if (!deleted) {
        this.error = "Unable to delete document."
        return
      }

      await this.loadDocuments()
    },

    async download (document) {
      this.error = ""

      if (!document?.id) return

      const blob = await services.user.loanDocuments.download(this.business.id, document.id)

      if (!blob) {
        this.error = "Unable to download document."
        return
      }

      const url = window.URL.createObjectURL(blob)
      const link = window.document.createElement("a")

      link.href = url
      link.download = document.name || "document"
      window.document.body.appendChild(link)
      link.click()
      link.remove()

      window.URL.revokeObjectURL(url)
    },

    onProgress (progresses) {
      this.isLoanProfileCompleted = !progresses.slice(1,-1)?.some(progress => !progress.completed)
    },

    openDocuments () {
      this.showDocuments = true
    },

    closeDocuments () {
      this.showDocuments = false
    },

    async onSave(next) {
      this.error = ''
      this.loading = true
      try {
        const payload = Object.fromEntries(
          Object.entries(this.formData).filter(([, v]) => v !== null && v !== '')
        )
        this.loanBeacon = await services.loanBeacon.update(this.business.id, payload)
        next()
      } catch (error) {
        this.error = 'Failed to save loan profile.'
      } finally {
        this.loading = false
      }
    }
  },
};
</script>

<style scoped lang="scss">
.loan-beacon-widget {
  @include stack-space($space-large);

  .loan-beacon-header {
    @include inline-space($space-small);
    align-items: center;
    .bi {
      background: $color-green;
    }
  }

  .start-container {
    @include stack-space($space-medium);
    .loan-start {
      @include inline-space($space-medium);
      .loan-start-text {
        @include stack-space($space-none);
        flex: 1;
      }
      .action-description {
        color: $color-text-secondary;
      }
    }
  }

  .loan-information {
    @include stack-space($space-large);

    .hint {
      margin-top: -$space-small;
      color: $color-text-secondary;
    }
  }
}
</style>